import useSWR from 'swr';

import { IReviewsMeta } from '@/components/account/Tabs/Reviews/Rating/Rating';
import { TCurrency } from '@/config/locales';
import apiRequest from '@/services/apiRequest';
import searchApiRequest from '@/services/searchApiRequest';
import { IReview } from '@/services/types/core/reviews/id';
import { IRentalResponse } from '@/services/types/search/rentals/id';
import { IUserResponse } from '@/services/types/search/users/id';
import { getCoreApi, getSearchApi } from '@/utility/getCoreApi';

import { IOwnerLocationsResponse } from './types/core/ownerLocations';
import { IReportCard } from './types/owner';

export const getOwnerInfo = (slug: string): Promise<IUserResponse> => {
  const url = `${getSearchApi()}/users${Number.isNaN(Number(slug)) ? `?slug=${slug}` : `/${slug}`}`;
  return apiRequest<IUserResponse>({ url });
};

export const getOwnerRentals = (
  userId: number,
  limit = 8,
  currency: TCurrency,
  rentalCategory?: string,
): Promise<IRentalResponse | undefined> =>
  searchApiRequest<IRentalResponse>('rentals', {
    queryParams: {
      owner_id: userId,
      'page[limit]': limit,
      'filter[rental_category]': rentalCategory,
    },
    rejectOnError: true,
    currency,
  });

export const getOwnerReviews = async (
  userId: number,
  limit = 16,
  offset = 0,
): Promise<[IReview[], number]> => {
  const url = `${getCoreApi()}/reviews?user_id=${userId}&include_all=true&offset=${offset}${
    limit > 0 ? `&limit=${limit}` : ''
  }`;

  const response = await apiRequest<IReview[]>({ url }, true, true);

  return [response.data || [], Number(response.headers['total-results'])];
};

export const getOwnerLocations = async (userId: number): Promise<IOwnerLocationsResponse> => {
  const url = `${getCoreApi()}/locations/${userId}/for-pro-page?order=-rentals`;

  const locations = await apiRequest<IOwnerLocationsResponse>({ url });
  return locations || { data: [], fallback: {} };
};

export const getOwnerReportCard = (userId: number) => {
  const url = `${getCoreApi()}/users/${userId}/owner-report-card?include_all_rentals=true`;
  return apiRequest<IReportCard>(
    {
      url,
      method: 'GET',
    },
    true,
    true,
  );
};

export const useReviewsMeta = () => {
  return useSWR(`${getCoreApi()}/reviews-meta?include_all=true`, url =>
    apiRequest<IReviewsMeta>({ url }, true),
  );
};

const getReviews = async (userId?: string | number, limit = 5, offset = 0) => {
  return await apiRequest<IReview[]>(
    {
      url: `${getCoreApi()}/reviews?user_id=${userId}&limit=${limit}&offset=${offset}`,
      method: 'GET',
    },
    true,
    true,
  );
};

export const useReviews = (userId?: string | number, limit?: number, offset?: number) => {
  const { data, error, mutate, isValidating } = useSWR(
    userId ? [`user-${userId}`, limit, offset] : null,
    () => getReviews(userId, limit, offset),
  );

  const isLoading = !data && !error && isValidating;

  return {
    data: data,
    isError: error,
    mutate,
    isValidating,
    isLoading,
  };
};
